import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { refreshSession } from '../lib/session.js';
import { OwnerActivation } from './owner-activation.js';
import { PasswordRecovery } from './password-recovery.js';
import type { LoginMode } from './root-login.js';

/// As telas que as rotas de autenticação montam, com a navegação já amarrada.
///
/// Elas moram fora do arquivo de rotas porque lá também vive `createAppRouter`, que não é
/// componente — e um arquivo que mistura os dois quebra a recarga a quente do Vite.

/// Pedir o link e usar o link são dois endereços e uma tela só: o que muda entre `/forgot-password`
/// e `/reset-password` é ter token em mãos.
export function PasswordRecoveryScreen({
  token,
  mode,
}: {
  token: string | null;
  mode: LoginMode | undefined;
}) {
  const navigate = useNavigate();
  return (
    <PasswordRecovery
      token={token}
      /// Trocar em vez de empilhar: "voltar para entrar" é desfazer o caminho, e deixar a
      /// recuperação no histórico faria o gesto de voltar do celular devolver a pessoa para dentro
      /// de um fluxo que ela acabou de abandonar.
      onBack={() => void navigate({ to: '/login', search: { mode }, replace: true })}
      onUseDevelopmentToken={(developmentToken) => {
        void navigate({ to: '/reset-password', search: { mode, token: developmentToken } });
      }}
    />
  );
}

export function ActivationScreen({
  token,
  kind,
}: {
  token: string;
  kind: 'owner' | 'member' | 'client';
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return (
    <OwnerActivation
      token={token}
      kind={kind}
      onActivated={async () => {
        await refreshSession(queryClient);
        /// Trocar, e não empilhar: o link de ativação vale uma vez só, e voltar para ele mostraria
        /// um convite já consumido.
        // Cliente vai para a própria área; quem entrou numa empresa passa pelo portão do início.
        await navigate({ to: kind === 'client' ? '/account' : '/home', replace: true });
      }}
    />
  );
}

/// O que aparece enquanto o portão da rota pergunta quem é a pessoa. É a mesma tela que existia
/// antes de haver rota — o que mudou é quem a mostra.
export function PendingSession() {
  return (
    <div className="grid min-h-screen place-items-center bg-surface">
      <div
        className="size-8 animate-spin rounded-full border-2 border-brand-500/25 border-t-brand-500"
        aria-label="Carregando sessão"
      />
    </div>
  );
}
